// =============================================================================
// FILE: packages/renderer/src/lib/technicalProofreader.ts
// PURPOSE: "Technical Proofreader" sub-agent. A deterministic, offline
//   Auto-Polish pass that runs before any PDF/DXF is generated: fixes common
//   spelling/spacing/capitalization issues while PRESERVING technical
//   BIM/Telecom acronyms (RCDD, BICSI, ANSI/TIA, MDF, PoE, OS2, DXF, …).
// =============================================================================

export const PROTECTED_TERMS = new Set([
  'RCDD', 'BICSI', 'ANSI', 'TIA', 'EIA', 'MDF', 'IDF', 'ODF', 'UTP', 'STP', 'POE', 'PoE',
  'OS2', 'OM3', 'OM4', 'PDU', 'KVM', 'UPS', 'BOM', 'DXF', 'DWG', 'SVG', 'PDF', 'BIM',
  'RCD2', 'MEP', 'HVAC', 'BACNET', 'SNMP', 'MQTT', 'SYSTIMAX', 'UFC', 'FAA', 'AIA',
  'RU', 'TMGB', 'SPD', 'OSP', 'CAT6', 'CAT6A', 'CCTV', 'IoT', 'AP', 'PON', 'M365',
]);

const COMMON_FIXES: Record<string, string> = {
  teh: 'the', adn: 'and', recieve: 'receive', adress: 'address', managment: 'management',
  seperate: 'separate', occured: 'occurred', definately: 'definitely', equipmnet: 'equipment',
  instalation: 'installation', buidling: 'building', cabeling: 'cabling', maintenence: 'maintenance',
  lenght: 'length', widht: 'width', concreet: 'concrete', manhoel: 'manhole', ductbnak: 'ductbank',
};

export interface Correction { from: string; to: string }
export interface ProofResult { text: string; corrections: Correction[] }

function isProtected(word: string): boolean {
  return PROTECTED_TERMS.has(word.toUpperCase()) || PROTECTED_TERMS.has(word);
}

/** Auto-Polish a single string; returns the polished text + the corrections made. */
export function proofread(input: string): ProofResult {
  const corrections: Correction[] = [];
  if (!input) return { text: input, corrections };

  // 1) collapse repeated whitespace, trim stray spaces before punctuation
  let out = input.replace(/[ \t]{2,}/g, ' ').replace(/\s+([,.;:])/g, '$1').trim();

  // 2) token-level spelling fixes (skip protected acronyms; preserve case)
  out = out.replace(/[A-Za-z][A-Za-z0-9-]*/g, (word) => {
    if (isProtected(word)) return word;
    const fix = COMMON_FIXES[word.toLowerCase()];
    if (fix) {
      const cased = /^[A-Z]/.test(word) ? fix.charAt(0).toUpperCase() + fix.slice(1) : fix;
      corrections.push({ from: word, to: cased });
      return cased;
    }
    return word;
  });

  // 3) capitalize the first letter of each sentence (leave protected acronyms intact)
  out = out.replace(/(^|[.!?]\s+)([a-z])/g, (_m, p1: string, p2: string) => p1 + (p2 ?? '').toUpperCase());

  return { text: out, corrections };
}

/** Polish every field of an object of strings; aggregates the correction count. */
export function autoPolishFields<T extends Record<string, string>>(fields: T): { polished: T; total: number } {
  const polished = { ...fields };
  let total = 0;
  (Object.keys(fields) as Array<keyof T>).forEach((k) => {
    const r = proofread(fields[k] ?? '');
    polished[k] = r.text as T[keyof T];
    total += r.corrections.length;
  });
  return { polished, total };
}
